import { ReactNode } from 'react';
import { Box, IconButton, SxProps, Theme, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { SGTooltip } from 'components/core';

interface SectionShellProps {
  title: string;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onRemove?: () => void;
  headerActions?: ReactNode;
  children: ReactNode;
  hidden?: boolean;
  sx?: SxProps<Theme>;
}

// Collapsible, removable card wrapper shared by every section of the
// Options Calculator's layout (Header / P/L Chart / Heatmap / Positions
// Table, plus any extra sections a user inserts).
const SectionShell = ({
  title,
  collapsed,
  onToggleCollapsed,
  onRemove,
  headerActions,
  children,
  hidden = false,
  sx,
}: SectionShellProps) => {
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 2,
        p: '12px',
        display: hidden ? 'none' : 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: collapsed ? 0 : 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
            sx={{ color: 'text.secondary' }}
          >
            {collapsed ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ExpandLessIcon fontSize="small" />
            )}
          </IconButton>
          <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {!collapsed && headerActions}
          {onRemove != null && (
            <SGTooltip title={`Remove ${title} section`}>
              <IconButton
                size="small"
                onClick={onRemove}
                aria-label={`Remove ${title}`}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </SGTooltip>
          )}
        </Box>
      </Box>
      {!collapsed && children}
    </Box>
  );
};

export default SectionShell;
